import {
  extractFileId,
  getFileMetadata,
  searchHtmlFiles,
  type DriveFile,
} from "../drive-api";
import { isConnected } from "../drive-auth";
import { addFromDrive } from "../store";

let overlay: HTMLDivElement | null = null;

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function renderShell(): string {
  return `
    <div class="picker-backdrop" data-action="close"></div>
    <div class="picker" role="dialog" aria-label="Adicionar arquivo do Drive">
      <header class="picker-head">
        <h2>Adicionar HTML do Drive</h2>
        <button class="ghost" data-action="close" aria-label="Fechar">×</button>
      </header>
      <div class="picker-body">
        <input
          class="picker-search"
          type="search"
          placeholder="Buscar no Drive (ou cole URL/ID do arquivo)"
          autocomplete="off"
        />
        <div class="picker-status" data-role="status">Carregando HTMLs recentes…</div>
        <ul class="picker-results" data-role="results"></ul>
      </div>
    </div>
  `;
}

function renderResults(files: DriveFile[]): string {
  if (files.length === 0) return "";
  return files
    .map(
      (f) => `
        <li>
          <button class="picker-result" data-pick="${f.id}">
            <div class="picker-result-name">${escapeHtml(f.name)}</div>
            <div class="picker-result-meta">${new Date(f.modifiedTime).toLocaleString("pt-BR")}</div>
          </button>
        </li>
      `,
    )
    .join("");
}

export function openPicker(): void {
  if (overlay) return;
  if (!isConnected()) {
    alert("Conecte o Drive antes de adicionar arquivos.");
    return;
  }

  overlay = document.createElement("div");
  overlay.className = "picker-overlay";
  overlay.innerHTML = renderShell();
  document.body.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>(".picker-search")!;
  const status = overlay.querySelector<HTMLElement>('[data-role="status"]')!;
  const results = overlay.querySelector<HTMLElement>('[data-role="results"]')!;

  let activeQuery = 0;
  let lastResults: DriveFile[] = [];
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  async function runSearch(query: string): Promise<void> {
    const myTurn = ++activeQuery;
    status.textContent = query ? "Buscando…" : "Carregando HTMLs recentes…";
    try {
      const files = await searchHtmlFiles(query, 30);
      if (myTurn !== activeQuery) return;
      lastResults = files;
      results.innerHTML = renderResults(files);
      status.textContent =
        files.length === 0
          ? "Nenhum HTML encontrado."
          : query
            ? `${files.length} resultado(s)`
            : `${files.length} HTML(s) mais recentes`;
    } catch (err) {
      if (myTurn !== activeQuery) return;
      status.textContent = `Erro: ${(err as Error).message}`;
    }
  }

  async function tryPasteAsId(value: string): Promise<void> {
    const id = extractFileId(value);
    if (!id) return;
    status.textContent = "Validando arquivo colado…";
    try {
      const file = await getFileMetadata(id);
      addFromDrive(file);
      close();
    } catch (err) {
      status.textContent = `Não consegui abrir esse ID: ${(err as Error).message}`;
    }
  }

  input.addEventListener("input", () => {
    const value = input.value;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (value.includes("/") || value.startsWith("http")) {
      void tryPasteAsId(value);
      return;
    }
    if (value.length === 1) {
      status.textContent = "Digite ao menos 2 caracteres…";
      results.innerHTML = "";
      lastResults = [];
      return;
    }
    debounceTimer = setTimeout(() => {
      void runSearch(value);
    }, 300);
  });

  overlay.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    if (target.dataset.action === "close") return close();
    const pickId = target.closest<HTMLElement>("[data-pick]")?.dataset.pick;
    if (pickId) {
      const file = lastResults.find((f) => f.id === pickId);
      if (file) {
        addFromDrive(file);
        close();
      }
    }
  });

  document.addEventListener("keydown", onEscape);
  input.focus();
  void runSearch("");
}

function onEscape(ev: KeyboardEvent): void {
  if (ev.key === "Escape") close();
}

function close(): void {
  if (!overlay) return;
  document.removeEventListener("keydown", onEscape);
  overlay.remove();
  overlay = null;
}
